/**
 * ReturningUserManager - Manages returning user experience via localStorage
 *
 * Stores information about recently logged-in users so they can quickly
 * log back in using their saved passkey or password.
 *
 * Storage Format:
 * - Key: 'metalark_returning_users'
 * - Value: Array of user objects, sorted by lastLoginAt (most recent first)
 * - Max users stored: 5
 *
 * - Key: 'metalark_do_not_remember'
 * - Value: Array of userUuids who opted out of being remembered
 */

const STORAGE_KEY = 'metalark_returning_users';
const DO_NOT_REMEMBER_KEY = 'metalark_do_not_remember';
const MAX_STORED_USERS = 5;
const STORAGE_VERSION = 1;

/**
 * User data structure stored in localStorage
 * @typedef {Object} StoredUser
 * @property {string} userUuid - User's UUID
 * @property {string} email - User's email
 * @property {string} name - User's display name
 * @property {boolean} hasPasskey - Whether user has passkey configured
 * @property {boolean} hasPassword - Whether user has password configured
 * @property {number} lastLoginAt - Timestamp of last login
 * @property {number} version - Storage schema version
 */

export class ReturningUserManager {
  /**
   * Save or update user information after successful login
   * 
   * @param {Object} userData - User data from authentication
   * @param {string} userData.userUuid - User's UUID
   * @param {string} userData.email - User's email
   * @param {string} userData.name - User's display name
   * @param {boolean} userData.hasPasskey - Whether user has passkey
   * @param {boolean} userData.hasPassword - Whether user has password
   */
  static saveUser(userData) {
    try {
      // Check if user has opted out of being remembered
      if (this.isDoNotRemember(userData.userUuid)) {
        console.log('User has opted out of being remembered:', userData.userUuid);
        return;
      }

      const users = this.getStoredUsers();
      
      // Remove existing entry for this user if present
      const filteredUsers = users.filter(u => u.userUuid !== userData.userUuid);
      
      // Create new user entry
      const newUser = {
        userUuid: userData.userUuid,
        email: userData.email,
        name: userData.name || userData.email,
        hasPasskey: userData.hasPasskey || false,
        hasPassword: userData.hasPassword || false,
        lastLoginAt: Date.now(),
        version: STORAGE_VERSION
      };
      
      // Add to front of array (most recent)
      filteredUsers.unshift(newUser);
      
      // Keep only MAX_STORED_USERS most recent users
      const trimmedUsers = filteredUsers.slice(0, MAX_STORED_USERS);
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedUsers));
      
      console.log('Saved returning user:', newUser.email);
    } catch (error) {
      console.error('Failed to save returning user:', error);
    }
  }

  /**
   * Update user information after successful login, but do not create an entry if we don't have one
   * 
   * @param {Object} userData - User data from authentication
   * @param {string} userData.userUuid - User's UUID
   * @param {string} userData.email - User's email
   * @param {string} userData.name - User's display name
   * @param {boolean} userData.hasPasskey - Whether user has passkey
   * @param {boolean} userData.hasPassword - Whether user has password
   */
  static updateUser(userData) {

    try {
      // Check if user has opted out of being remembered
      if (this.isDoNotRemember(userData.userUuid)) {
        console.log('User has opted out of being remembered:', userData.userUuid);
        return;
      }

      const users = this.getStoredUsers();
      
      const myUser = users.filter(u => u.userUuid === userData.userUuid);
      
      // Remove existing entry for this user if present
      const filteredUsers = users.filter(u => u.userUuid !== userData.userUuid);
      
      // only add the user if it was already there (we don't add an entry for passkey-only userse)
      if (myUser.length > 0) {
        // Create new user entry
        const newUser = {
          userUuid: userData.userUuid,
          email: userData.email,
          name: userData.name || userData.email,
          hasPasskey: userData.hasPasskey || false,
          hasPassword: userData.hasPassword || false,
          lastLoginAt: Date.now(),
          version: STORAGE_VERSION
        };
        
        // Add to front of array (most recent)
        filteredUsers.unshift(newUser);
        
        console.log('Saved returning user:', newUser.email);
      }
      
      // Keep only MAX_STORED_USERS most recent users
      const trimmedUsers = filteredUsers.slice(0, MAX_STORED_USERS);
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmedUsers));
    } catch (error) {
      console.error('Failed to save returning user:', error);
    }
  }

  /**
   * Get list of returning users
   * Sorted by most recent login first
   *
   * @returns {StoredUser[]} Array of returning users
   */
  static getReturningUsers() {
    try {
      const users = this.getStoredUsers();
      
      // Filter out users older than 90 days
      const ninetyDaysAgo = Date.now() - (90 * 24 * 60 * 60 * 1000);
      let validUsers = users.filter(u => u.lastLoginAt > ninetyDaysAgo);
      
      // Filter out users with blank/invalid critical fields
      validUsers = validUsers.filter(u => this.isValidUser(u));
      
      // Update storage if we filtered out any users (old or invalid)
      if (validUsers.length !== users.length) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(validUsers));
        console.log(`Cleaned up returning users: removed ${users.length - validUsers.length} invalid/expired entries`);
      }
      
      return validUsers;
    } catch (error) {
      console.error('Failed to get returning users:', error);
      return [];
    }
  }

  /**
   * Get a specific user by UUID
   * 
   * @param {string} userUuid - User's UUID
   * @returns {StoredUser|null} User data or null if not found
   */
  static getUser(userUuid) {
    const users = this.getReturningUsers();
    return users.find(u => u.userUuid === userUuid) || null;
  }

  /**
   * Get a specific user by email
   * 
   * @param {string} email - User's email
   * @returns {StoredUser|null} User data or null if not found
   */
  static getUserByEmail(email) {
    const users = this.getReturningUsers();
    return users.find(u => u.email.toLowerCase() === email.toLowerCase()) || null;
  }

  /**
   * Get the most recent user
   * 
   * @returns {StoredUser|null} Most recently logged in user or null
   */
  static getMostRecentUser() {
    const users = this.getReturningUsers();
    return users.length > 0 ? users[0] : null;
  }

  /**
   * Get the last logged in user (alias for getMostRecentUser)
   *
   * @returns {StoredUser|null} Most recently logged in user or null
   */
  static getLastUser() {
    return this.getMostRecentUser();
  }

  /**
   * Get users with passkeys
   * 
   * @returns {StoredUser[]} Array of users with passkeys configured
   */
  static getUsersWithPasskeys() {
    return this.getReturningUsers().filter(u => u.hasPasskey);
  }

  /**
   * Remove a user from returning users list
   *
   * @param {string} userUuid - User's UUID to remove
   * @param {boolean} doNotRemember - If true, remember preference to not show this user again
   */
  static removeUser(userUuid, doNotRemember = false) {
    try {
      const users = this.getStoredUsers();
      const filteredUsers = users.filter(u => u.userUuid !== userUuid);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(filteredUsers));
      
      // If user opted to not be remembered, add to do-not-remember list
      if (doNotRemember) {
        this.addToDoNotRemember(userUuid);
      }
      
      console.log('Removed returning user:', userUuid, { doNotRemember });
    } catch (error) {
      console.error('Failed to remove returning user:', error);
    }
  }

  /**
   * Clear all returning users
   */
  static clearAll() {
    try {
      localStorage.removeItem(STORAGE_KEY);
      console.log('Cleared all returning users');
    } catch (error) {
      console.error('Failed to clear returning users:', error);
    }
  }

  /**
   * Update user's authentication method flags
   * Call this when user adds/removes passkey or password
   * 
   * @param {string} userUuid - User's UUID
   * @param {Object} updates - Fields to update
   * @param {boolean} [updates.hasPasskey] - Whether user has passkey
   * @param {boolean} [updates.hasPassword] - Whether user has password
   */
  static updateUserAuthMethods(userUuid, updates) {
    try {
      const users = this.getStoredUsers();
      const userIndex = users.findIndex(u => u.userUuid === userUuid);
      
      if (userIndex !== -1) {
        users[userIndex] = {
          ...users[userIndex],
          ...updates
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(users));
        console.log('Updated user auth methods:', userUuid, updates);
      }
    } catch (error) {
      console.error('Failed to update user auth methods:', error);
    }
  }

  /**
   * Check if there are any returning users
   * 
   * @returns {boolean} True if there are returning users
   */
  static hasReturningUsers() {
    return this.getReturningUsers().length > 0;
  }

  /**
   * Get raw stored users from localStorage
   * @private
   */
  static getStoredUsers() {
    try {
      const data = localStorage.getItem(STORAGE_KEY);
      if (!data) {
        return [];
      }
      
      const users = JSON.parse(data);
      
      // Validate data structure
      if (!Array.isArray(users)) {
        console.warn('Invalid returning users data, resetting');
        return [];
      }
      
      // Migrate old versions if needed
      return users.map(user => this.migrateUser(user));
    } catch (error) {
      console.error('Failed to parse returning users:', error);
      return [];
    }
  }

  /**
   * Validate that a user has all required fields with non-blank values
   * @private
   */
  static isValidUser(user) {
    // Check if user object exists
    if (!user || typeof user !== 'object') {
      return false;
    }
    
    // Check required string fields are present and not blank
    const hasValidUuid = user.userUuid && typeof user.userUuid === 'string' && user.userUuid.trim() !== '';
    const hasValidEmail = user.email && typeof user.email === 'string' && user.email.trim() !== '';
    const hasValidName = user.name && typeof user.name === 'string' && user.name.trim() !== '';
    
    return hasValidUuid && hasValidEmail && hasValidName;
  }

  /**
   * Migrate user data from old versions
   * @private
   */
  static migrateUser(user) {
    // Ensure all required fields exist
    return {
      userUuid: user.userUuid || user.uuid || '',
      email: user.email || '',
      name: user.name || user.email || '',
      hasPasskey: user.hasPasskey || false,
      hasPassword: user.hasPassword || false,
      lastLoginAt: user.lastLoginAt || Date.now(),
      version: STORAGE_VERSION
    };
  }

  /**
   * Get formatted time since last login
   * 
   * @param {number} lastLoginAt - Timestamp of last login
   * @returns {string} Human-readable time string
   */
  static getTimeSinceLogin(lastLoginAt) {
    const now = Date.now();
    const diff = now - lastLoginAt;
    
    const minutes = Math.floor(diff / (1000 * 60));
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    if (days < 30) return `${days}d ago`;
    return 'Over a month ago';
  }

  /**
   * Add user to do-not-remember list
   * @private
   */
  static addToDoNotRemember(userUuid) {
    try {
      const list = this.getDoNotRememberList();
      if (!list.includes(userUuid)) {
        list.push(userUuid);
        localStorage.setItem(DO_NOT_REMEMBER_KEY, JSON.stringify(list));
        console.log('Added to do-not-remember list:', userUuid);
      }
    } catch (error) {
      console.error('Failed to add to do-not-remember list:', error);
    }
  }

  /**
   * Check if user has opted out of being remembered
   * @private
   */
  static isDoNotRemember(userUuid) {
    const list = this.getDoNotRememberList();
    return list.includes(userUuid);
  }

  /**
   * Get do-not-remember list
   * @private
   */
  static getDoNotRememberList() {
    try {
      const data = localStorage.getItem(DO_NOT_REMEMBER_KEY);
      if (!data) return [];
      const list = JSON.parse(data);
      return Array.isArray(list) ? list : [];
    } catch (error) {
      console.error('Failed to get do-not-remember list:', error);
      return [];
    }
  }

  /**
   * Remove user from do-not-remember list
   * Use this if user wants to be remembered again
   *
   * @param {string} userUuid - User's UUID
   */
  static removeFromDoNotRemember(userUuid) {
    try {
      const list = this.getDoNotRememberList();
      const filtered = list.filter(uuid => uuid !== userUuid);
      localStorage.setItem(DO_NOT_REMEMBER_KEY, JSON.stringify(filtered));
      console.log('Removed from do-not-remember list:', userUuid);
    } catch (error) {
      console.error('Failed to remove from do-not-remember list:', error);
    }
  }

  /**
   * Clear do-not-remember list
   */
  static clearDoNotRemember() {
    try {
      localStorage.removeItem(DO_NOT_REMEMBER_KEY);
      console.log('Cleared do-not-remember list');
    } catch (error) {
      console.error('Failed to clear do-not-remember list:', error);
    }
  }

  /**
   * Check if we should show the passkey registration prompt for a user
   *
   * @param {string} userUuid - User's UUID
   * @returns {boolean} True if prompt should be shown
   */
  static shouldShowPasskeyPrompt(userUuid) {
    try {
      const user = this.getUser(userUuid);
      if (user?.hasPasskey) {
        return false;
      }

      const dismissedKey = `passkey-prompt-dismissed-${userUuid}`;
      const dismissed = localStorage.getItem(dismissedKey);
      if (dismissed) {
        const dismissedAt = parseInt(dismissed, 10);
        const daysSinceDismissed = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
        if (daysSinceDismissed < 7) {
          return false;
        }
      }

      return true;
    } catch (error) {
      console.error('Failed to check if passkey prompt should be shown:', error);
      return false;
    }
  }

  /**
   * Mark passkey prompt as dismissed for a user
   *
   * @param {string} userUuid - User's UUID
   */
  static dismissPasskeyPrompt(userUuid) {
    try {
      const dismissedKey = `passkey-prompt-dismissed-${userUuid}`;
      localStorage.setItem(dismissedKey, Date.now().toString());
      console.log('Passkey prompt dismissed for user:', userUuid);
    } catch (error) {
      console.error('Failed to mark passkey prompt as dismissed:', error);
    }
  }

  /**
   * Clear passkey prompt dismissal for a user
   *
   * @param {string} userUuid - User's UUID
   */
  static clearPasskeyPromptDismissal(userUuid) {
    try {
      const dismissedKey = `passkey-prompt-dismissed-${userUuid}`;
      localStorage.removeItem(dismissedKey);
      console.log('Cleared passkey prompt dismissal for user:', userUuid);
    } catch (error) {
      console.error('Failed to clear passkey prompt dismissal:', error);
    }
  }
}